/**
 * What the applicant is told at a decision, and the careful part is what is NOT here.
 *
 * `ApplicationDecision` is `{ outcome, decidedAt, messageToApplicant }` and its own description
 * scopes it: "No reviewer identity and no internal note is projected here." There is no reviewer
 * name available anywhere on this surface, so none is invented, and the message is rendered
 * VERBATIM — it is the one piece of writing on this page that is not ours.
 *
 * THERE IS NO REJECTION AND THIS COMPONENT SAYS SO. `work_queue_decision` seeds exactly two
 * outcomes for kind `application`, and `author_transition_rule` has no `applicant -> disabled`
 * row: there is no terminal refused state, and a refused applicant may re-apply indefinitely. So
 * "needs more information" is worded as an invitation and never as a verdict, and the screen
 * states plainly that the door stays open — because the person reading it will assume otherwise.
 */
import { Alert, Blockquote, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import type { ApplicationDecision } from '../contract';
import { narrowOutcome } from '../domain/states';

export interface DecisionNoticeProps {
  readonly decision: ApplicationDecision;
  readonly round: number | null | undefined;
}

export function DecisionNotice({ decision, round }: DecisionNoticeProps): ReactNode {
  const { t } = useTranslation('application');
  const outcome = narrowOutcome(decision.outcome);

  if (outcome === 'approved') {
    return (
      <Alert
        variant="light"
        title={t('decision.approved.title')}
        style={{ borderColor: 'oklch(var(--mx-success) / 0.35)' }}
      >
        <Stack gap="xs">
          {/* It used to add "Your page address is now fixed", which is false for an author approved
              with no address yet - they may still choose one, once. The status header says which
              is true, from `OwnProfile.slugFrozen` (`addressRule`, domain/states.ts; design 14,
              F3), so this notice no longer says anything about the address. */}
          <Text size="sm">{t('decision.approved.body')}</Text>
          {decision.messageToApplicant !== null && decision.messageToApplicant !== undefined ? (
            <Blockquote p="sm" radius="sm">
              <Text size="sm">{decision.messageToApplicant}</Text>
            </Blockquote>
          ) : null}
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert
      variant="light"
      title={
        round !== null && round !== undefined && round > 1
          ? t('decision.moreInfo.afterRound', { round })
          : t('decision.moreInfo.title')
      }
      style={{ borderColor: 'oklch(var(--mx-warning) / 0.35)' }}
    >
      <Stack gap="sm">
        {decision.messageToApplicant !== null && decision.messageToApplicant !== undefined ? (
          <Blockquote p="sm" radius="sm" cite={t('decision.moreInfo.cite')}>
            <Text size="sm">{decision.messageToApplicant}</Text>
          </Blockquote>
        ) : (
          // The message is nullable, so this branch is real. Saying "no note was left" is honest;
          // inventing a reason would not be.
          <Text size="sm">{t('decision.moreInfo.noNote')}</Text>
        )}
        <Text size="xs" c="dimmed">
          {t('decision.moreInfo.notARefusal')}
        </Text>
      </Stack>
    </Alert>
  );
}
